import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, 
  Rocket,
  Mail,
  Download,
  Search,
  Loader2,
  Building2,
  Phone,
  Calendar,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const AdminNewsletterPage = () => {
  const [copiloteWaitlist, setCopiloteWaitlist] = useState([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [waitlistRes, subscribersRes] = await Promise.all([
        api.get('/newsletter/copilote-waitlist').catch(() => ({ data: [] })),
        api.get('/newsletter/subscribers').catch(() => ({ data: [] }))
      ]);
      setCopiloteWaitlist(waitlistRes.data || []);
      setNewsletterSubscribers(subscribersRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data.length) {
      toast.error('Aucune donnée à exporter');
      return;
    }
    
    const headers = Object.keys(data[0]).filter(k => k !== 'id' && k !== '_id');
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Export téléchargé');
  };

  const filteredWaitlist = copiloteWaitlist.filter(item => 
    item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a365d]">Newsletter & Inscriptions</h1>
          <p className="text-[#64748b]">Gérez les inscriptions Copilote et la newsletter</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-500 flex items-center justify-center">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-[#1a365d]">{copiloteWaitlist.length}</p>
                <p className="text-sm text-[#64748b]">Waitlist Copilote</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-[#1a365d]">{newsletterSubscribers.length}</p>
                <p className="text-sm text-[#64748b]">Newsletter</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-[#1a365d]">50</p>
                <p className="text-sm text-[#64748b]">Places early access</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="copilote" className="space-y-6">
        <TabsList>
          <TabsTrigger value="copilote">
            <Rocket className="w-4 h-4 mr-2" />
            Waitlist Copilote ({copiloteWaitlist.length})
          </TabsTrigger>
          <TabsTrigger value="newsletter">
            <Mail className="w-4 h-4 mr-2" />
            Newsletter ({newsletterSubscribers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="copilote" className="space-y-4">
          {/* Search & Export */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => exportToCSV(copiloteWaitlist, 'copilote_waitlist')}
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </Button>
          </div>

          {/* Waitlist Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">#</th>
                      <th className="text-left p-4 font-medium text-slate-600">Contact</th>
                      <th className="text-left p-4 font-medium text-slate-600">Entreprise</th>
                      <th className="text-left p-4 font-medium text-slate-600">Téléphone</th>
                      <th className="text-left p-4 font-medium text-slate-600">Date</th>
                      <th className="text-left p-4 font-medium text-slate-600">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredWaitlist.map((item, idx) => (
                      <tr key={item.id || idx} className="hover:bg-slate-50">
                        <td className="p-4">
                          <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                            #{item.position || idx + 1}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-[#1a365d]">
                              {item.first_name} {item.last_name}
                            </p>
                            <p className="text-sm text-slate-500">{item.email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          {item.company ? (
                            <div className="flex items-center gap-2 text-slate-600">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              {item.company}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          {item.phone ? (
                            <div className="flex items-center gap-2 text-slate-600">
                              <Phone className="w-4 h-4 text-slate-400" />
                              {item.phone}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Calendar className="w-4 h-4" />
                            {item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : '-'}
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge 
                            variant={item.status === 'contacted' ? 'default' : 'secondary'}
                            className={item.status === 'contacted' ? 'bg-emerald-100 text-emerald-700' : ''}
                          >
                            {item.status === 'contacted' ? 'Contacté' : 'En attente'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredWaitlist.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Rocket className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucune inscription pour le moment</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="newsletter" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Email</th>
                      <th className="text-left p-4 font-medium text-slate-600">Source</th>
                      <th className="text-left p-4 font-medium text-slate-600">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {newsletterSubscribers.map((item, idx) => (
                      <tr key={item.id || idx} className="hover:bg-slate-50">
                        <td className="p-4 font-medium text-[#1a365d]">{item.email}</td>
                        <td className="p-4 text-slate-600">{item.source || 'Site web'}</td>
                        <td className="p-4 text-sm text-slate-500">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {newsletterSubscribers.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun abonné newsletter</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button 
              variant="outline" 
              onClick={() => exportToCSV(newsletterSubscribers, 'newsletter_subscribers')}
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminNewsletterPage;
